// Peças comuns da aba Bem-Estar: dias da semana, marcador de feito, seção que recolhe.
import { useMemo, useRef, useState, type ReactNode } from 'react';
import { Alert, Animated, PanResponder, Pressable, Text, View } from 'react-native';
import { getDoc, useDoc } from '../../store/store';
import { alternarSecao, excluirCuidado, excluirRefeicao, excluirSuplemento, excluirTreino } from '../../store/bemestar';
import { C } from '../theme';
import { Recolhivel } from '../Recolhivel';
import { t } from '../../lib/i18n';
import { diaCurto } from '../../lib/date';
import { paraLixeira } from '../../lib/lixeira';
import type { AlertaItem } from '../../store/types';
import { AlertasEditor } from '../AlertasEditor';
import { paraOLado } from '../../lib/gestos';
import { useAreaRolagem } from '../Arrastar';

/** Lixeira de 30 dias (0.65): guarda uma cópia do item (refeição, cuidado, treino ou suplemento) e depois exclui. */
export type CampoBE = 'refeicoes' | 'cuidados' | 'treinos' | 'suplementos';
const EXCLUIR_BE: Record<CampoBE, (id: string) => void> = { refeicoes: excluirRefeicao, cuidados: excluirCuidado, treinos: excluirTreino, suplementos: excluirSuplemento };
export function apagarBE(campo: CampoBE, id: string) {
  const x = ((getDoc('bemestar')[campo] ?? []) as { id: string; nome: string }[]).find(y => y.id === id);
  if (x) paraLixeira('bemestar', 'lista', x.nome, x, { doc: 'bemestar', campo });
  EXCLUIR_BE[campo](id);
}
/** Confirmação padrão de exclusão (vai para a lixeira). */
export const confirmarExcluirBE = (nome: string, fn: () => void) => Alert.alert(t('Excluir {nome}?', { nome: nome || t('este item') }), t('Fica 30 dias na lixeira.'), [
  { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: fn }]);

/** Dia da semana abreviado (Seg, Ter, Qua...). Antes era uma letra só e ficava "S S S" (0.52). */
export const DL = (i: number) => diaCurto(i);
export const diasLabel = (d: number[]) => d.length === 7 ? t('Todo dia') : d.join() === '1,2,3,4,5' ? t('Seg a sex') : d.join() === '0,6' ? t('Fim de semana')
  : [1, 2, 3, 4, 5, 6, 0].filter(x => d.includes(x)).map(x => DL(x)).join(' ') || t('Nenhum dia');
export const horaDate = (h: string | null) => { const d = new Date(); const [a, b] = (h ?? '08:00').split(':').map(Number); d.setHours(a, b, 0, 0); return d; };

export function Dias({ v, on, cor }: { v: number[]; on: (d: number[]) => void; cor: string }) {
  return (
    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap' }}>
      {[1, 2, 3, 4, 5, 6, 0].map(i => (
        <Pressable key={i} onPress={() => on(v.includes(i) ? v.filter(x => x !== i) : [...v, i].sort())}
          style={{ width: 38, height: 38, borderRadius: 19, alignItems: 'center', justifyContent: 'center', backgroundColor: v.includes(i) ? cor : C.card2 }}>
          <Text style={{ color: v.includes(i) ? '#fff' : C.sub, fontWeight: '700', fontSize: 12 }} numberOfLines={1} adjustsFontSizeToFit>{DL(i)}</Text>
        </Pressable>
      ))}
    </View>
  );
}

export function Check({ on, cor, onPress }: { on: boolean; cor: string; onPress: () => void }) {
  return (
    <Pressable hitSlop={8} onPress={onPress} style={{ width: 30, height: 30, borderRadius: 15, borderWidth: 2, borderColor: cor,
      backgroundColor: on ? cor : 'transparent', alignItems: 'center', justifyContent: 'center' }}>
      {on && <Text style={{ color: '#fff', fontWeight: '900' }}>✓</Text>}
    </Pressable>
  );
}

/** Título de seção: tocar esconde e mostra o conteúdo (a escolha fica salva na aba Bem-estar).
 *  Usa o componente único de seção recolhível (components/Recolhivel.tsx), com a seta à esquerda como sempre foi aqui. */
export function Secao({ id, titulo, direita, children }: { id: string; titulo: string; direita?: ReactNode; children: ReactNode }) {
  const recolhidas = useDoc('bemestar').recolhidas ?? [];
  const fechada = recolhidas.includes(id);
  return (
    <Recolhivel visual="titulo" seta="esquerda" titulo={titulo} estado={!fechada} onAlternar={() => alternarSecao(id)} gap={8} hitSlop={8}
      direita={!fechada ? direita : undefined} estiloBarra={{ marginTop: 8, paddingVertical: 4, gap: 6 }} estiloTitulo={{ color: C.sub }}
      estiloSeta={{ color: C.mute, fontSize: 12 }}>
      {children}
    </Recolhivel>
  );
}

/** Arrastar o cartão para a direita exclui (pede confirmação), igual às caixas da aba Anotações. */
/** antes: confirmação extra antes de perguntar (ex.: senha ou biometria para excluir um fichário inteiro). */
export function SwipeExcluir({ nome, onExcluir, children, antes, aviso }: { nome: string; onExcluir: () => void; children: ReactNode; antes?: () => Promise<boolean>; aviso?: string }) {
  const x = useRef(new Animated.Value(0)).current;
  const larg = useRef(320);
  const [ativo, setAtivo] = useState(false);
  const voltar = () => Animated.spring(x, { toValue: 0, useNativeDriver: false, bounciness: 6 }).start(() => setAtivo(false));
  const area = useAreaRolagem();
  const areaRef = useRef(area); areaRef.current = area;
  const pan = useMemo(() => {
    const horizontal = (_: unknown, g: { dx: number; dy: number }) => g.dx > 0 && paraOLado(g);   // texto 28 (A5)
    return PanResponder.create({
      onMoveShouldSetPanResponder: horizontal,
      onMoveShouldSetPanResponderCapture: horizontal,
      onPanResponderTerminationRequest: () => false,
      onPanResponderGrant: () => { setAtivo(true); areaRef.current?.setRolagem(false); },
      onPanResponderMove: (_, g) => x.setValue(Math.max(0, g.dx)),
      onPanResponderRelease: (_, g) => {
        areaRef.current?.setRolagem(true);
        voltar();
        if (g.dx <= larg.current * 0.35) return;
        const perguntar = () => Alert.alert(t('Excluir {nome}?', { nome: nome || t('este item') }), aviso ?? '', [
          { text: t('Cancelar'), style: 'cancel' }, { text: t('Excluir'), style: 'destructive', onPress: onExcluir }]);
        if (antes) antes().then(ok => { if (ok) perguntar(); }, () => {}); else perguntar();
      },
      onPanResponderTerminate: () => { areaRef.current?.setRolagem(true); voltar(); },
    });
  }, [nome, onExcluir, antes, aviso]); // eslint-disable-line react-hooks/exhaustive-deps
  const op = x.interpolate({ inputRange: [0, 40], outputRange: [0, 1], extrapolate: 'clamp' });
  return (
    <View onLayout={e => { larg.current = e.nativeEvent.layout.width; }}>
      {ativo && (
        <Animated.View pointerEvents="none" style={{ position: 'absolute', top: 0, bottom: 0, left: 0, right: 0, borderRadius: 14, backgroundColor: C.danger, opacity: op,
          justifyContent: 'center', paddingLeft: 18 }}>
          <Text style={{ color: '#fff', fontWeight: '800' }}>🗑 {t('Excluir')}</Text>
        </Animated.View>
      )}
      <Animated.View {...pan.panHandlers} style={{ transform: [{ translateX: x }] }}>{children}</Animated.View>
    </View>
  );
}

/** Bolinhas da semana (seg a dom): cheia = feito, vazada = previsto e não feito, apagada = não era dia. */
export function SemanaFeitos({ id, dias, cor }: { id: string; dias: number[]; cor: string }) {
  const d = useDoc('bemestar');
  const hoje = new Date(); const dow = hoje.getDay();
  const seg = new Date(hoje); seg.setDate(hoje.getDate() - ((dow + 6) % 7));
  return (
    <View style={{ flexDirection: 'row', gap: 4, marginTop: 4 }}>
      {Array.from({ length: 7 }, (_, i) => {
        const x = new Date(seg); x.setDate(seg.getDate() + i);
        const data = `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
        const previsto = dias.includes(x.getDay());
        const feito = (d.feitos[data] ?? []).includes(id);
        const futuro = x > hoje;
        return (
          <View key={i} style={{ alignItems: 'center', gap: 1, minWidth: 22 }}>
            <View style={{ width: 12, height: 12, borderRadius: 6, borderWidth: 1.5, borderColor: previsto ? cor : C.line,
              backgroundColor: feito ? cor : 'transparent', opacity: futuro && !feito ? 0.4 : 1 }} />
            <Text style={{ color: C.mute, fontSize: 8 }}>{DL(x.getDay())}</Text>
          </View>
        );
      })}
    </View>
  );
}

/** Alertas de uma rotina do Bem-Estar (texto 10, A4): o mesmo editor da Nova tarefa, nos dias marcados.
 *  Só avisa no celular; "Enviar para o Planner" é outra escolha. */
export function AlertasDaRotina({ itens, hora, cor, onChange }: { itens?: AlertaItem[]; hora: string | null; cor: string; onChange: (l: AlertaItem[]) => void }) {
  return (
    <View style={{ gap: 6 }}>
      <Text style={{ color: C.sub, fontSize: 12 }}>🔔 {t('Alertas')}</Text>
      <AlertasEditor itens={itens ?? []} onChange={onChange} temHorario={!!hora} horaPadrao={hora} cor={cor} aba="bemestar" />
    </View>
  );
}
